import { useCallback, useEffect, useState } from 'react';
import { ApolloError, WatchQueryFetchPolicy } from '@apollo/client';
import {
  FreeSpinPromo,
  FreeTicketPromo,
  GigCommonPromo,
  GigPromo,
  MagicXPromo,
  ParsedPromo,
  UserGigQuery,
} from '../../components/Layout/Promotions/types';
import { useUserContext } from '../../providers/UserProvider';
import { UserProfileBonusStatus } from '../../types.__generated__';
import { xTenantId } from '../../consts';
import { Endpoint } from '../../models/api.model';
import {
  useTagsObjectLazyQuery,
  TagsObjectQuery,
} from '../../libs/graphql/baseAppAPI/queries/__generated__/tags-get.query.generated';
import {
  UserFreeSpinQuery,
  useUserFreeSpinLazyQuery,
} from '../../libs/graphql/baseAppAPI/queries/__generated__/user-free-spin-get.generated';
import { getActiveMxPromotions, getAvailablegMxPromotions } from '../../components/Layout/Promotions/services';
import { PlatformPromos, PromoTagCategory, UserTags } from './types';
import { getMagicXData } from '../../components/Layout/Promotions/utils';
import { useAuth } from 'react-oidc-context';
import { useDispatch, useSelector } from 'react-redux';
import { loadPlatformPromotions } from '../../state/promotions';
import { setLoadPlatformPromotions } from '../../state/promotions/reducers';
import {
  UserFreeTicketQuery,
  useUserFreeTicketLazyQuery,
} from '../../libs/graphql/baseAppAPI/queries/__generated__/user-free-ticket-get.generated';

export const useFetchPlatformPromos = (): PlatformPromos => {
  const { userProfileId, user } = useUserContext();
  const { isAuthenticated } = useAuth();
  const dispatch = useDispatch();
  const loadPlatformPromotionsState = useSelector(loadPlatformPromotions);

  const [isPromosLoading, setIsPromotionsLoading] = useState<boolean>(true);
  const [fetchMagicXError, setFetchMagicXError] = useState<unknown>('');
  const [userTags, setUserTags] = useState<Nullable<UserTags>>(null);
  const [magicXPromos, setMagicXPromos] = useState<ParsedPromo[]>([]);
  const [rewardsPromos, setRewardsPromos] = useState<ParsedPromo[]>([]);
  const [isAlreadyFetched, setIsAlreadyFetched] = useState<boolean>(false);

  const onQueryError = (e: ApolloError): void => console.error(e.message);

  const handleRewards = useCallback((data: UserGigQuery): void => {
    const gigFreeTicket = (data as UserFreeTicketQuery).userFreeTicket?.data;
    const gigFreeSpins = (data as UserFreeSpinQuery).userFreeSpin?.data;

    const resGigBonuses = gigFreeTicket || gigFreeSpins || [];

    const matchedRewards: ParsedPromo[] = (resGigBonuses as GigCommonPromo[])
      .map((gigPromo: GigCommonPromo) => {
        const freeSpin = gigPromo as FreeSpinPromo;
        const freeTicket = gigPromo as FreeTicketPromo;

        const resPromoId = freeSpin?.freeSpinsTemplateId || freeTicket?.freeTicketsTemplateId;

        return resPromoId
          ? {
              id: freeSpin?.id || freeTicket?.id,
              bonusId: resPromoId,
              status: (gigPromo as GigPromo)?.status,
              expiresOn: (gigPromo as GigPromo)?.expiresOn,
            }
          : null;
      })
      .filter(Boolean) as ParsedPromo[];

    setRewardsPromos((prev) => [...prev, ...matchedRewards]);
  }, []);

  const handleMagicXPromos = (magicXPromotions: MagicXPromo[], { isActive }: { isActive: boolean }): ParsedPromo[] => {
    // First, handle the Gig bonuses merging as before
    const mergedMagicXPromotions: ParsedPromo[] = magicXPromotions.map((magicXPromo) => {
      const {
        id: magicId,
        expiry: expiresOn,
        start: createdOn,
        milestones,
        achievementValue: wageredAmount,
        winAmount: balanceAmount,
        type,
        slug: magicXSlug,
      } = magicXPromo;
      const { value: wageringRequirementAmount, level: convertedAmount } = milestones?.[0] || {};
      const { magicXCta, magicXAvailabilityTagName, magicXActivityTagName } = getMagicXData(magicXPromo);

      const status = isActive ? UserProfileBonusStatus.Claimed : UserProfileBonusStatus.Available;

      return {
        magicId,
        status,
        expiresOn,
        createdOn,
        wageringRequirementAmount,
        wageredAmount,
        balanceAmount,
        convertedAmount,
        magicXCta,
        magicXAvailabilityTagName,
        magicXActivityTagName,
        magicXSlug,
        cancellable: false,
        type,
      };
    });

    return mergedMagicXPromotions;
  };

  const handleUserTags = (userTagsData: TagsObjectQuery): void => {
    setUserTags(
      userTagsData.tagsObject
        ?.filter((item) => item.tag?.tagCategory?.name === PromoTagCategory.BONUS)
        .map((item) => ({ tagId: item.tag?.id, tagName: item.tag?.name }))
    );
  };

  const fetchRewards = {
    variables: {
      xTenantId,
      userProfileId,
      pageLimit: 1000,
    },
    fetchPolicy: 'network-only' as WatchQueryFetchPolicy,
    onCompleted: handleRewards,
    notifyOnNetworkStatusChange: true,
    onError: onQueryError,
    context: { endpoint: Endpoint.appAPI },
  };

  const [fetchBonusesFreeSpinGig, { error: fetchBonusesFreeSpinGigError }] = useUserFreeSpinLazyQuery(fetchRewards);

  const [fetchBonusesFreeTicketGig, { error: fetchBonusesFreeTicketGigError }] =
    useUserFreeTicketLazyQuery(fetchRewards);

  const [fetchUserTags, { error: fetchUserTagsError }] = useTagsObjectLazyQuery({
    variables: {
      xTenantId,
      externalResource: 'UserProfileId',
      externalResourceId: userProfileId,
    },
    fetchPolicy: 'network-only' as WatchQueryFetchPolicy,
    onCompleted: handleUserTags,
    notifyOnNetworkStatusChange: true,
    onError: onQueryError,
    context: { endpoint: Endpoint.appAPI },
  });

  const promotionsLoadingError =
    fetchBonusesFreeSpinGigError || fetchBonusesFreeTicketGigError || fetchUserTagsError || fetchMagicXError;

  const fetchMagicXPromotions = async (): Promise<void> => {
    try {
      const activeMXPromotions = await getActiveMxPromotions(user?.email);
      const receivedAvailableMXPromotions = await getAvailablegMxPromotions(user?.email);

      // Filter promotions that may be available and active at the same time
      const availableMXPromotions = receivedAvailableMXPromotions.filter(
        (availablePromo) => !activeMXPromotions.find((activePromo) => activePromo.slug === availablePromo.slug)
      );

      const resActiveMxPromos = handleMagicXPromos(activeMXPromotions, { isActive: true });
      const resAvailableMxPromos = handleMagicXPromos(availableMXPromotions, { isActive: false });

      setMagicXPromos([...resActiveMxPromos, ...resAvailableMxPromos]);
    } catch (error) {
      setFetchMagicXError(error);
    }
  };

  useEffect(() => {
    (async (): Promise<void> => {
      if (user?.email && loadPlatformPromotionsState) {
        setIsPromotionsLoading(true);

        if (!isAlreadyFetched) {
          await fetchBonusesFreeSpinGig();
          await fetchBonusesFreeTicketGig();
        }

        await fetchUserTags();
        await fetchMagicXPromotions();

        setIsPromotionsLoading(false);
        setIsAlreadyFetched(true);
        dispatch(setLoadPlatformPromotions(false));
      }
    })();
  }, [user?.email, loadPlatformPromotionsState]);

  useEffect(() => {
    dispatch(setLoadPlatformPromotions(true));
  }, []);

  useEffect(() => {
    !isAuthenticated && setIsPromotionsLoading(false);
  }, [isAuthenticated]);

  return {
    platformPromos: {
      magicXPromos,
      rewardsPromos,
      userTags,
    },
    isPromosLoading,
    promosLoadingError: promotionsLoadingError,
  };
};
